import React, {

  useEffect,

  useMemo,

  useState,

} from "react";

import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import "./SupplierStatements.css";

import SupplierSidebar from "./SupplierSidebar";

import SupplierTopbar from "./SupplierTopbar";

import supplierPortalService from "../../services/supplierPortalService";
import * as XLSX from "xlsx";

const monthNames = [

  "January",

  "February",

  "March",

  "April",

  "May",

  "June",

  "July",

  "August",

  "September",

  "October",

  "November",

  "December",

];

const getMonthName = (month) => {

  return monthNames[Number(month) - 1] || "";

};

const getStatementNumber = (statement) => {

  return `ST-${statement?.statement_year || ""}-${String(

    statement?.statement_month || ""

  ).padStart(2, "0")}`;

};

const getVehicleNumber = (vehicle) => {

  if (!vehicle) return "";

  const value =

    vehicle?.vehicle_registration_number ||

    vehicle?.vehicle_registration_no ||

    vehicle?.registration_number ||

    vehicle?.registration_no ||

    vehicle?.vehicle?.vehicle_registration_number ||

    vehicle?.vehicle?.vehicle_registration_no ||

    vehicle?.vehicle?.registration_number ||

    vehicle?.vehicle?.registration_no ||

    "";

  return String(value).trim();

};

const getStatementVehicleNumber = (statement, vehicles) => {

  if (!statement) return "";

  const direct =

    statement.vehicle_registration_number ||

    statement.vehicle_registration_no ||

    statement.registration_number ||

    statement.registration_no ||

    statement.vehicle_display_number ||

    statement.vehicle_label ||

    statement.vehicle?.vehicle_registration_number ||

    statement.vehicle?.vehicle_registration_no ||

    statement.vehicle?.registration_number ||

    statement.vehicle?.registration_no;

  if (direct) return String(direct).trim();

  const statementVehicleId =

    statement.vehicle_id ??

    statement.vehicle?.id ??

    statement.vehicle?.vehicle_id ??

    statement.vehicle_assignment?.vehicle_id ??

    statement.vehicle_assignment?.vehicle?.id;

  if (statementVehicleId == null || !Array.isArray(vehicles)) return "";

  const match = vehicles.find((vehicle) => {

    const ids = [vehicle?.id, vehicle?.vehicle_id, vehicle?.vehicle?.id];

    return ids.some((id) => id != null && String(id) === String(statementVehicleId));

  });

  return getVehicleNumber(match);

};

const resolveFileUrl = (value) => {

  if (!value) return "";

  const url = String(value).trim();

  if (!url) return "";

  const apiBase =

    process.env.REACT_APP_API_URL ||

    process.env.REACT_APP_API_BASE_URL ||

    "https://srinivasafleet.com/api";

  const backendOrigin = apiBase.replace(/\/api\/?$/, "");

  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) {

    try {

      const parsed = new URL(url);

      if (parsed.pathname.startsWith("/uploads/")) {

        return `${backendOrigin}${parsed.pathname}${parsed.search}`;

      }

      return url;

    } catch {

      return url;

    }

  }

  if (url.startsWith("/")) return `${backendOrigin}${url}`;

  if (url.startsWith("uploads/")) return `${backendOrigin}/${url}`;

  return `${backendOrigin}/${url}`;

};

const getDownloadFileName = (statement) => {

  const statementNumber =

    getStatementNumber(statement);

  const fileUrl =

    statement?.file_url || "";

  try {

    const url = new URL(

      fileUrl,

      window.location.origin

    );

    const pathname = url.pathname || "";

    const lastPart = pathname

      .split("/")

      .pop();

    if (

      lastPart &&

      (

        lastPart

          .toLowerCase()

          .endsWith(".xlsx") ||

        lastPart

          .toLowerCase()

          .endsWith(".xls")

      )

    ) {

      return decodeURIComponent(lastPart);

    }

  } catch (error) {

    void error;

  }

  return `Supplier_Statement\_${statementNumber}.xlsx`;

};

const SupplierStatements = () => {

  const navigate = useNavigate();

  const [supplier, setSupplier] =

    useState(null);

  const [statements, setStatements] =

    useState([]);

  const [allStatements, setAllStatements] = useState([]);

  const [vehicles, setVehicles] =

    useState([]);

  const [vehicleNumber, setVehicleNumber] =

    useState("All");

  const [year, setYear] =

    useState("All");

  const [month, setMonth] =

    useState("All");

  const [search, setSearch] =

    useState("");

  const [loading, setLoading] =

    useState(true);

  const [errorMessage, setErrorMessage] =

    useState("");

  const [sidebarOpen, setSidebarOpen] =

    useState(false);

  const [downloadingId, setDownloadingId] =

    useState(null);

  const [previewData, setPreviewData] = useState(null);

  const [previewTitle, setPreviewTitle] = useState("");

  const [previewLoading, setPreviewLoading] = useState(false);

  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (previewData === null) return undefined;
    const sidebarElements = Array.from(
      document.querySelectorAll('[class*="sidebar"], [class*="Sidebar"], [id*="sidebar"], [id*="Sidebar"]')
    );
    const previousStyles = sidebarElements.map((element) => ({
      element,
      zIndex: element.style.getPropertyValue("z-index"),
      priority: element.style.getPropertyPriority("z-index"),
    }));
    sidebarElements.forEach((element) => {
      element.style.setProperty("z-index", "1", "important");
    });
    document.body.style.setProperty("isolation", "isolate");
    return () => {
      previousStyles.forEach(({ element, zIndex, priority }) => {
        if (zIndex) element.style.setProperty("z-index", zIndex, priority);
        else element.style.removeProperty("z-index");
      });
      document.body.style.removeProperty("isolation");
    };
  }, [previewData]);

  useEffect(() => {

    let mounted = true;

    const loadStatements = async () => {

      try {

        setLoading(true);

        setErrorMessage("");

        if (

          !supplierPortalService.isLoggedIn()

        ) {

          navigate("/supplier/login", {

            replace: true,

          });

          return;

        }

        const [

          profileResponse,

          statementsResponse,

          vehiclesResponse,

        ] = await Promise.all([

          supplierPortalService.getMe(),

          supplierPortalService.getStatements(),

          supplierPortalService.getVehicles(),

        ]);

        if (!mounted) return;

        const profile =

          profileResponse?.supplier ||

          profileResponse ||

          null;

        const statementList =

          statementsResponse?.statements ||

          statementsResponse?.data ||

          [];

        const vehicleList =

          vehiclesResponse?.vehicles ||

          vehiclesResponse?.data ||

          vehiclesResponse ||

          [];

        setSupplier(profile);

        const normalizedStatements = Array.isArray(statementList)

          ? statementList

          : [];

        setAllStatements(normalizedStatements);

        setStatements(normalizedStatements);

        setVehicles(

          Array.isArray(vehicleList)

            ? vehicleList

            : []

        );

      } catch (error) {

        if (!mounted) return;

        if (

          !supplierPortalService.isLoggedIn()

        ) {

          navigate("/supplier/login", {

            replace: true,

          });

          return;

        }

        setErrorMessage(

          error?.message ||

            "Unable to load supplier statements."

        );

      } finally {

        if (mounted) {

          setLoading(false);

        }

      }

    };

    loadStatements();

    return () => {

      mounted = false;

    };

  }, [navigate]);

  const vehicleNumbers = useMemo(() => {

    const uniqueVehicles = [

      ...new Set(

        vehicles

          .map((vehicle) =>

            getVehicleNumber(vehicle)

          )

          .filter(Boolean)

      ),

    ];

    return uniqueVehicles.sort(

      (a, b) =>

        a.localeCompare(b, undefined, {

          numeric: true,

          sensitivity: "base",

        })

    );

  }, [vehicles]);

  const years = useMemo(() => {

    const uniqueYears = [

      ...new Set(

        statements

          .map(

            (item) =>

              item?.statement_year

          )

          .filter(

            (itemYear) =>

              itemYear !== null &&

              itemYear !== undefined

          )

      ),

    ];

    return uniqueYears.sort(

      (a, b) => Number(b) - Number(a)

    );

  }, [statements]);

  const months = useMemo(() => {

    const uniqueMonths = [

      ...new Set(

        statements

          .map((item) =>

            Number(item?.statement_month)

          )

          .filter(

            (itemMonth) =>

              itemMonth >= 1 &&

              itemMonth <= 12

          )

      ),

    ];

    return uniqueMonths.sort(

      (a, b) => a - b

    );

  }, [statements]);

  const filteredStatements = useMemo(() => {

    const searchValue = search.trim().toLowerCase();

    const targetVehicle =

      vehicleNumber === "All"

        ? ""

        : String(vehicleNumber).trim().toLowerCase();

    const vehicleMatches = (vehicle) => {

      if (!vehicle) return false;

      const values = [

        vehicle?.vehicle_registration_number,

        vehicle?.vehicle_registration_no,

        vehicle?.registration_number,

        vehicle?.registration_no,

        vehicle?.vehicle_display_number,

        vehicle?.vehicle_label,

      ];

      return values.some(

        (value) =>

          String(value || "").trim().toLowerCase() ===

          targetVehicle

      );

    };

    const statementMatchesVehicle = (item) => {

      if (!targetVehicle) return true;

      if (Array.isArray(item?.vehicles)) {

        if (item.vehicles.some(vehicleMatches)) {

          return true;

        }

      }

      const statementVehicle = getStatementVehicleNumber(

        item,

        vehicles

      );

      return (

        statementVehicle.trim().toLowerCase() ===

        targetVehicle

      );

    };

    return [...statements]

      .sort((a, b) => {

        const aValue =

          Number(a?.statement_year || 0) * 100 +

          Number(a?.statement_month || 0);

        const bValue =

          Number(b?.statement_year || 0) * 100 +

          Number(b?.statement_month || 0);

        return bValue - aValue;

      })

      .filter((item) => {

        const statementNumber =

          getStatementNumber(item).toLowerCase();

        const itemMonth = getMonthName(

          item?.statement_month

        ).toLowerCase();

        const statementYear = String(

          item?.statement_year || ""

        );

        const itemVehicleNumber =

          getStatementVehicleNumber(item, vehicles);

        const matchesSearch =

          !searchValue ||

          statementNumber.includes(searchValue) ||

          itemMonth.includes(searchValue) ||

          statementYear.includes(searchValue) ||

          itemVehicleNumber

            .toLowerCase()

            .includes(searchValue);

        const matchesYear =

          year === "All" ||

          String(item?.statement_year) === String(year);

        const matchesMonth =

          month === "All" ||

          String(item?.statement_month) === String(month);

        return (

          matchesSearch &&

          statementMatchesVehicle(item) &&

          matchesYear &&

          matchesMonth

        );

      });

  }, [

    statements,

    vehicles,

    vehicleNumber,

    year,

    month,

    search,

  ]);

  const selectedVehicleOutstanding = useMemo(() => {
    if (!Array.isArray(vehicles) || vehicles.length === 0) {
      return 0;
    }

    // Show combined outstanding for all vehicles.
    if (vehicleNumber === "All") {
      return vehicles.reduce(
        (total, vehicle) =>
          total + Number(vehicle?.outstanding_amount || 0),
        0
      );
    }

    // Show outstanding for the selected vehicle.
    const selectedVehicle = vehicles.find(
      (vehicle) =>
        getVehicleNumber(vehicle).trim().toLowerCase() ===
        String(vehicleNumber).trim().toLowerCase()
    );

    return Number(selectedVehicle?.outstanding_amount || 0);
  }, [vehicles, vehicleNumber]);

  const supplierName =

    supplier?.supplier_name ||

    "Supplier";

  const supplierCode =

    supplier?.supplier_code || "—";

  const handleView = async (statement) => {
    if (!statement?.file_url || previewLoading) return;
    const fileUrl = resolveFileUrl(statement.file_url);
    setPreviewTitle(getStatementNumber(statement));
    setPreviewData([]);
    setPreviewError("");
    setPreviewLoading(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`Unable to load file (${response.status}).`);
      const buffer = await response.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error("The Excel file does not contain a worksheet.");
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
        header: 1,
        defval: "",
        raw: false,
      });
      setPreviewData(rows);
    } catch (error) {
      setPreviewError(error?.message || "Unable to preview this file. Check that the file exists and the backend allows access to uploaded files.");
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (

    statement

  ) => {

    if (

      !statement?.file_url ||

      downloadingId === statement?.id

    ) {

      return;

    }

    const fileUrl = resolveFileUrl(

      statement.file_url

    );

    if (!fileUrl) return;

    const downloadName =

      getDownloadFileName(statement);

    try {

      setDownloadingId(statement?.id);

      const response = await fetch(

        fileUrl,

        {

          method: "GET",

          credentials: "include",

        }

      );

      if (!response.ok) {

        throw new Error(

          `Download failed: ${response.status}`

        );

      }

      const blob =

        await response.blob();

      const blobUrl =

        window.URL.createObjectURL(blob);

      const link =

        document.createElement("a");

      link.href = blobUrl;

      link.download = downloadName;

      link.style.display = "none";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {

      const link =

        document.createElement("a");

      link.href = fileUrl;

      link.download = downloadName;

      link.target = "_self";

      link.rel = "noopener";

      document.body.appendChild(link);

      link.click();

      link.remove();

    } finally {

      setDownloadingId(null);

    }

  };

  if (loading) {

    return (

      <div className="supplier-dashboard">

        <SupplierSidebar

          open={sidebarOpen}

          onClose={() =>

            setSidebarOpen(false)

          }

        />

        <div className="supplier-main">

          <SupplierTopbar

            supplier={supplier}

            sidebarOpen={sidebarOpen}

            onMenuClick={() =>

              setSidebarOpen(

                (value) => !value

              )

            }

          />

          <main className="supplier-dashboard-content">

            <div className="supplier-statements-loading">

              <div className="supplier-loading-spinner" />

              <p>

                Loading statements...

              </p>

            </div>

          </main>

        </div>

      </div>

    );

  }

  return (

    <div className="supplier-dashboard">

      <SupplierSidebar

        open={sidebarOpen}

        onClose={() =>

          setSidebarOpen(false)

        }

      />

      <div className="supplier-main">

        <SupplierTopbar

          supplier={supplier}

          sidebarOpen={sidebarOpen}

          onMenuClick={() =>

            setSidebarOpen(

              (value) => !value

            )

          }

        />

        <main className="supplier-dashboard-content">

          {errorMessage && (

            <div className="supplier-statements-error">

              {errorMessage}

            </div>

          )}

          <section className="supplier-statements-card">

            <div className="supplier-statements-title-row">

              <p>

                Monthly statements for{" "}

                <strong>

                  {supplierName}

                </strong>

              </p>

            </div>

            <div className="supplier-statement-filters">

              <div className="supplier-filter-field">

                <label htmlFor="statement-vehicle">

                  Vehicle Number

                </label>

                <select

                  id="statement-vehicle"

                  value={vehicleNumber}

                  onChange={(event) =>

                    setVehicleNumber(

                      event.target.value

                    )

                  }

                >

                  <option value="All">

                    All Vehicles

                  </option>

                  {vehicleNumbers.map(

                    (itemVehicle) => (

                      <option

                        key={itemVehicle}

                        value={itemVehicle}

                      >

                        {itemVehicle}

                      </option>

                    )

                  )}

                </select>

              </div>

              <div className="supplier-filter-field">

                <label htmlFor="statement-year">

                  Year

                </label>

                <select

                  id="statement-year"

                  value={year}

                  onChange={(event) =>

                    setYear(

                      event.target.value

                    )

                  }

                >

                  <option value="All">

                    All Years

                  </option>

                  {years.map((itemYear) => (

                    <option

                      key={itemYear}

                      value={itemYear}

                    >

                      {itemYear}

                    </option>

                  ))}

                </select>

              </div>

              <div className="supplier-filter-field">

                <label htmlFor="statement-month">

                  Month

                </label>

                <select

                  id="statement-month"

                  value={month}

                  onChange={(event) =>

                    setMonth(

                      event.target.value

                    )

                  }

                >

                  <option value="All">

                    All Months

                  </option>

                  {months.map(

                    (itemMonth) => (

                      <option

                        key={itemMonth}

                        value={itemMonth}

                      >

                        {getMonthName(

                          itemMonth

                        )}

                      </option>

                    )

                  )}

                </select>

              </div>

              <div className="supplier-outstanding-filter">

                <span className="supplier-outstanding-label">

                  OUTSTANDING

                </span>

                <strong>

                  ₹

                  {selectedVehicleOutstanding.toLocaleString(

                    "en-IN",

                    {

                      minimumFractionDigits: 2,

                      maximumFractionDigits: 2,

                    }

                  )}

                </strong>

              </div>

            </div>

            {filteredStatements.length ===

            0 ? (

              <div className="supplier-statements-empty">

                <div className="supplier-empty-icon">

                  📄

                </div>

                <h3>

                  No statements found

                </h3>

                <p>

                  No supplier statements are

                  available for the selected

                  filters.

                </p>

              </div>

            ) : (

              <>

                <div className="supplier-statements-desktop-table">

                  <div className="supplier-statements-table-wrap">

                    <table className="supplier-statements-table">

                      <colgroup>

                        <col className="col-vehicle" />

                        <col className="col-statement" />

                        <col className="col-month" />

                        <col className="col-year" />

                        <col className="col-document" />

                        <col className="col-action" />

                      </colgroup>

                      <thead>

                        <tr>

                          <th>

                            Vehicle Number

                          </th>

                          <th>

                            Statement

                          </th>

                          <th>

                            Month

                          </th>

                          <th>

                            Year

                          </th>

                          <th>

                            Document

                          </th>

                          <th>

                            Action

                          </th>

                        </tr>

                      </thead>

                      <tbody>

                        {filteredStatements.map(

                          (item) => {

                            const itemVehicle =

                              getStatementVehicleNumber(

                                item,

                                vehicles

                              );

                            return (

                              <tr

                                key={

                                  item.id ||

                                  getStatementNumber(

                                    item

                                  )

                                }

                              >

                                <td>

                                  <strong

                                    title={

                                      itemVehicle

                                    }

                                  >

                                    {itemVehicle ||

                                      "—"}

                                  </strong>

                                </td>

                                <td>

                                  <strong>

                                    {getStatementNumber(

                                      item

                                    )}

                                  </strong>

                                </td>

                                <td>

                                  {getMonthName(

                                    item.statement_month

                                  )}

                                </td>

                                <td>

                                  {

                                    item.statement_year

                                  }

                                </td>

                                <td>

                                  <span

                                    className={

                                      item.file_url

                                        ? "supplier-paid"

                                        : "supplier-pending"

                                    }

                                  >

                                    {item.file_url

                                      ? "Available"

                                      : "Not Available"}

                                  </span>

                                </td>

                                <td>

                                  <div className="supplier-statement-actions">

                                    <button

                                      type="button"

                                      className="supplier-view-btn"

                                      disabled={

                                        !item.file_url

                                      }

                                      onClick={() =>

                                        handleView(

                                          item

                                        )

                                      }

                                    >

                                      View

                                    </button>

                                    <button

                                      type="button"

                                      className="supplier-download-btn"

                                      disabled={

                                        !item.file_url ||

                                        downloadingId ===

                                          item.id

                                      }

                                      onClick={() =>

                                        handleDownload(

                                          item

                                        )

                                      }

                                    >

                                      {downloadingId ===

                                      item.id

                                        ? "Downloading..."

                                        : "Download"}

                                    </button>

                                  </div>

                                </td>

                              </tr>

                            );

                          }

                        )}

                      </tbody>

                    </table>

                  </div>

                </div>

                <div className="supplier-statements-mobile-list">

                  {filteredStatements.map(

                    (item) => {

                      const itemVehicle =

                        getStatementVehicleNumber(

                          item,

                          vehicles

                        );

                      return (

                        <article

                          className="supplier-statement-mobile-card"

                          key={

                            item.id ||

                            getStatementNumber(

                              item

                            )

                          }

                        >

                          <div className="supplier-mobile-card-head">

                            <div>

                              <span className="supplier-mobile-card-label">

                                STATEMENT

                              </span>

                              <h3>

                                {getStatementNumber(

                                  item

                                )}

                              </h3>

                            </div>

                            <span

                              className={

                                item.file_url

                                  ? "supplier-mobile-document-status available"

                                  : "supplier-mobile-document-status unavailable"

                              }

                            >

                              {item.file_url

                                ? "AVAILABLE"

                                : "NOT AVAILABLE"}

                            </span>

                          </div>

                          <div className="supplier-mobile-card-divider" />

                          <div className="supplier-mobile-card-details">

                            <div>

                              <span>

                                VEHICLE NUMBER

                              </span>

                              <strong>

                                {itemVehicle ||

                                  "—"}

                              </strong>

                            </div>

                            <div>

                              <span>

                                MONTH

                              </span>

                              <strong>

                                {getMonthName(

                                  item.statement_month

                                )}

                              </strong>

                            </div>

                            <div>

                              <span>

                                YEAR

                              </span>

                              <strong>

                                {

                                  item.statement_year

                                }

                              </strong>

                            </div>

                          </div>

                          <div className="supplier-mobile-file-row">

                            <div className="supplier-mobile-file-icon">

                              📄

                            </div>

                            <div>

                              <span>

                                DOCUMENT

                              </span>

                              <strong>

                                Supplier Statement

                              </strong>

                            </div>

                          </div>

                          <div className="supplier-mobile-statement-actions">

                            <button

                              type="button"

                              className="supplier-mobile-view-btn"

                              disabled={

                                !item.file_url

                              }

                              onClick={() =>

                                handleView(

                                  item

                                )

                              }

                            >

                              <span>◉</span>

                              <span>

                                View Statement

                              </span>

                            </button>

                            <button

                              type="button"

                              className="supplier-mobile-download-btn"

                              disabled={

                                !item.file_url ||

                                downloadingId ===

                                  item.id

                              }

                              onClick={() =>

                                handleDownload(

                                  item

                                )

                              }

                            >

                              <span>↓</span>

                              <span>

                                {downloadingId ===

                                item.id

                                  ? "Downloading..."

                                  : "Download"}

                              </span>

                            </button>

                          </div>

                        </article>

                      );

                    }

                  )}

                </div>

              </>

            )}

            {previewData !== null && createPortal((
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Statement preview"
                onClick={(event) => {
                  if (event.target === event.currentTarget) {
                    setPreviewData(null);
                    setPreviewError("");
                  }
                }}
                style={{ position: "fixed", inset: 0, zIndex: 2147483647, background: "rgba(8, 24, 48, 0.68)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}
              >
                <div style={{ width: "min(1400px, 96vw)", maxWidth: "96vw", maxHeight: "88vh", background: "#fff", borderRadius: "16px", boxShadow: "0 20px 60px rgba(0,0,0,.3)", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative", margin: "auto" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", padding: "16px 20px", borderBottom: "1px solid #dbe5f0" }}>
                    <div>
                      <strong style={{ color: "#123d70", fontSize: "18px" }}>{previewTitle}</strong>
                      <div style={{ color: "#64748b", fontSize: "12px", marginTop: "4px" }}>Excel statement preview</div>
                    </div>
                    <button type="button" onClick={() => { setPreviewData(null); setPreviewError(""); }} aria-label="Close preview" style={{ border: "1px solid #d5dfeb", background: "#fff", borderRadius: "8px", width: "38px", height: "38px", cursor: "pointer", fontSize: "22px" }}>×</button>
                  </div>
                  {previewLoading ? (
                    <div style={{ padding: "32px", color: "#64748b" }}>Loading statement preview...</div>
                  ) : previewError ? (
                    <div style={{ padding: "24px", color: "#b91c1c" }}>{previewError}</div>
                  ) : (
                    <div style={{ overflow: "auto", padding: "16px" }}>
                      {previewData.length === 0 ? <p style={{ color: "#64748b" }}>This worksheet is empty.</p> : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#17324f" }}>
                          <tbody>
                            {previewData.map((row, rowIndex) => (
                              <tr key={`preview-row-${rowIndex}`}>
                                {row.map((cell, cellIndex) => {
                                  const CellTag = rowIndex === 0 ? "th" : "td";
                                  return <CellTag key={`preview-cell-${rowIndex}-${cellIndex}`} style={{ border: "1px solid #dbe5f0", padding: "9px 10px", textAlign: "left", whiteSpace: "pre-wrap", background: rowIndex === 0 ? "#eaf3fc" : "#fff", fontWeight: rowIndex === 0 ? 700 : 400 }}>{String(cell ?? "")}</CellTag>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ), document.body)}

            <div className="supplier-statements-footer">

              Showing{" "}

              <strong>

                {filteredStatements.length}

              </strong>{" "}

              of{" "}

              <strong>

                {statements.length}

              </strong>{" "}

              statements

            </div>

          </section>

        </main>

      </div>

    </div>

  );

};

export default SupplierStatements;
